'use client';
import { useScroll, useTransform, useSpring, motion } from 'framer-motion';
import { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';

export function ZoomParallax({
    images
}) {
	const container = useRef(null);
	const { scrollYProgress } = useScroll({
		target: container,
		offset: ['start start', 'end end'],
		layoutEffect: false, // Optimize for mobile - skip layout measurements
	});

	// Check for reduced motion preference for accessibility
	// Initialize with lazy state to prevent SSR/hydration issues
	const [prefersReducedMotion, setPrefersReducedMotion] = useState(() => {
		if (typeof window === 'undefined') return false;
		return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
	});

	useEffect(() => {
		const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
		setPrefersReducedMotion(mediaQuery.matches);

		const handleChange = () => setPrefersReducedMotion(mediaQuery.matches);
		mediaQuery.addEventListener('change', handleChange);
		return () => mediaQuery.removeEventListener('change', handleChange);
	}, []);

	// Apply smooth spring physics to scroll progress to prevent jumps on Safari iOS
	// Disable spring smoothing entirely if user prefers reduced motion
	const smoothProgress = prefersReducedMotion
		? scrollYProgress
		: useSpring(scrollYProgress, { stiffness: 100, damping: 30, restDelta: 0.001 });

	const scale4 = useTransform(smoothProgress, [0, 1], [1, 4]);
	const scale5 = useTransform(smoothProgress, [0, 1], [1, 5]);
	const scale6 = useTransform(smoothProgress, [0, 1], [1, 6]);
	const scale8 = useTransform(smoothProgress, [0, 1], [1, 8]);
	const scale9 = useTransform(smoothProgress, [0, 1], [1, 9]);

	const scales = [scale4, scale5, scale6, scale5, scale6, scale8, scale9];

	// Calculate overlay opacity based on scale transform
	const overlayOpacity = useTransform(scale4, (s) => {
		if (s <= 1) return 0;
		if (s <= 3) return Math.min((s - 1) * 0.425, 0.85);
		return Math.max(0.85 - (s - 3) * 0.2, 0);
	});

	return (
        <div
			ref={container}
			className="relative h-[300vh]"
		>
            <div className="sticky top-0 h-screen overflow-hidden" style={{ willChange: 'transform' }}>
				{images.map(({ src, alt }, index) => {
					const scale = scales[index % scales.length];

					return (
                        <motion.div
                            key={src || `img-${index}`}
                            style={{ scale, willChange: 'transform' }}
                            className={`absolute top-0 flex h-full w-full items-center justify-center ${index === 1 ? '[&>div]:!-top-[30vh] [&>div]:!left-[5vw] [&>div]:!h-[30vh] [&>div]:!w-[35vw]' : ''} ${index === 2 ? '[&>div]:!-top-[10vh] [&>div]:!-left-[25vw] [&>div]:!h-[45vh] [&>div]:!w-[20vw]' : ''} ${index === 3 ? '[&>div]:!left-[27.5vw] [&>div]:!h-[25vh] [&>div]:!w-[25vw]' : ''} ${index === 4 ? '[&>div]:!top-[27.5vh] [&>div]:!left-[5vw] [&>div]:!h-[25vh] [&>div]:!w-[20vw]' : ''} ${index === 5 ? '[&>div]:!top-[27.5vh] [&>div]:!-left-[22.5vw] [&>div]:!h-[25vh] [&>div]:!w-[30vw]' : ''} ${index === 6 ? '[&>div]:!top-[22.5vh] [&>div]:!left-[25vw] [&>div]:!h-[15vh] [&>div]:!w-[15vw]' : ''} `}>
                            <div className="relative h-[25vh] w-[25vw]">
                                <img
                                    src={src}
                                    alt={alt}
                                    className="h-full w-full object-cover" />
                                {/* Overlay text for concert crowd image */}
                                {index === 0 && (
                                    <motion.div
                                        style={{ scale, opacity: overlayOpacity }}
                                        className="absolute inset-0 flex items-center justify-center"
                                    >
                                        <div className="text-center w-3/4">
                                            {/* Desktop: Single line */}
                                            <h2 className="hidden md:block text-white text-xs md:text-sm font-bold font-['Space_Grotesk'] drop-shadow-2xl whitespace-nowrap mix-blend-luminosity">
                                                Building for Music Lovers
                                            </h2>
                                            {/* Mobile: Multi-line with forced breaks */}
                                            <h2 className="block md:hidden text-white text-xs font-bold font-['Space_Grotesk'] drop-shadow-2xl mix-blend-luminosity text-center leading-tight">
                                                Building<br />for Music<br />Lovers
                                            </h2>
                                        </div>
                                    </motion.div>
                                )}
                            </div>
                        </motion.div>
                    );
				})}
			</div>
        </div>
    );
}

ZoomParallax.propTypes = {
	images: PropTypes.arrayOf(
		PropTypes.shape({
			src: PropTypes.string.isRequired,
			alt: PropTypes.string.isRequired
		})
	).isRequired
};
